import { ref } from "vue";
import { defineStore } from "pinia";
import { useUser } from "@domain/Security/User";
import { useMarketplaceApi } from "./Api/MarketplaceApi";
import { MarketplaceViewModel } from "./MarketplaceViewModel";
import type { IMarketplaceViewModel } from "./IMarketplaceViewModel";
import { getGpsCoordinatesWithTimeout, getSearchBoundary } from "@services/GpsCoordinates";
import { useAppConfiguration } from "@domain/AppConfiguration";

const useStore = defineStore( 'marketplace-view-model-store', () =>
{
    const instance = ref<IMarketplaceViewModel>();
    return { instance };
} );

/**
 * Gets a new instance of the view model from the DTO returned from the API
 * @returns
 */
async function getViewModelInstance()
{

    const user = useUser();
    const api = useMarketplaceApi();
    const config = useAppConfiguration();

    // We need the user's current location in order to complete the filtering on the initial/landing page.
    // If the co-ordinates still haven't been retrieved within three seconds, assume we aren't getting them in order to prevent the page from waiting.
    // The only reason why this timeout should be executed is because the app is asking for permission to get the current location. If it already has permission or if it already has been denied permission, then it will exit/resolve straight away.
    const location = await getGpsCoordinatesWithTimeout( 3000 );

    const dto = await api.getViewModelAsync( user?.businessConsumerId!, user?.activeVehicle.vehicleId!, user?.activeVehicle.appConfigurationId!, getSearchBoundary( location, config.marketplaceRadiusKm ) );
    return new MarketplaceViewModel( dto!, location, config.marketplaceRadiusKm );
    
}

/**
 * Force  the view model to reload
 */
export async function reloadMarketplaceViewModel()
{
    const store = useStore();
    store.instance = await getViewModelInstance();
    return store.instance;
}

/**
 * Gets the current view model instance from the store
 * @returns
 */
export async function useViewModelInstance()
{
    const store = useStore();
    if( store.instance == null ) store.instance = await getViewModelInstance();
    return store.instance as IMarketplaceViewModel;
};

/**
 * True if the view model has been loaded
 * @returns 
 */
export function hasViewModelInstance()
{
    const store = useStore();
    return store.instance != null;
};

